// @todo Need to reconcile this with website-components. The main issue is we cannot style the button (https://grammarly.atlassian.net/browse/G2A-1917).
import React, { useCallback, useMemo, useRef } from 'react'

import { Button, InterfaceUploadIcon, Tooltip, TooltipContent, TooltipTrigger } from '@grammarly/design-system'

import { useApiService } from '@lib/hooks'
import {
  isFileSupportedExtensions,
  isFileNotBig,
  supportedFileExtensions,
  MAX_FILE_SIZE,
  getListOfFileTypesCopy,
} from '@components/tool/input/upload-button/utils'

export type UploadButtonError = 'FILE_ERROR_INVALID_EXT' | 'FILE_ERROR_TOO_BIG' | 'UNKNOWN_ERROR'
export interface UploadedFileInfo {
  fileName: string
  type: string
  size: number // in bytes
}

export interface UploadButtonProps {
  text?: string
  className?: string
  onClick?: () => void
  onFileChangeStart?: () => void
  onFileChangeFinish: (text: string, fileInfo: Partial<UploadedFileInfo>) => void
  onError?: (
    error: UploadButtonError,
    fileName: string | undefined,
    maxFileSize?: number,
    userFileSize?: number
  ) => void
  isDisabled?: boolean
  isLoading?: boolean
}

export const UploadButton: React.FC<UploadButtonProps> = ({
  text = 'Upload a file',
  className,
  onError,
  onFileChangeStart,
  onClick,
  onFileChangeFinish,
  isDisabled,
  isLoading,
}) => {
  const { dox: doxClient } = useApiService()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const onClickHandler = useCallback(() => {
    if (fileInputRef.current) {
      onClick?.()
      fileInputRef.current?.click()
    }
  }, [onClick])

  const loadFile = useCallback(
    async (file: File) => {
      if (!file) {
        return
      }

      const { name: fileName, size, type } = file

      if (!isFileSupportedExtensions(file)) {
        onError?.('FILE_ERROR_INVALID_EXT', fileName)

        return
      }

      if (!isFileNotBig(file)) {
        onError?.('FILE_ERROR_TOO_BIG', fileName, MAX_FILE_SIZE, size)

        return
      }

      onFileChangeStart?.()

      try {
        const text = await doxClient.extractPlainText(file)
        onFileChangeFinish(text, { fileName, type, size })
      } catch (err) {
        console.error(err)
        onError?.('UNKNOWN_ERROR', fileName)
      }
    },
    [doxClient, onError, onFileChangeFinish, onFileChangeStart]
  )

  const onUploadFileHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      await loadFile(event.target.files[0])

      event.target.value = ''
    }
  }

  const toolTipContent = useMemo(() => `Supported file types: ${getListOfFileTypesCopy('and')}`, [])

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="secondary"
            text={text}
            iconEnd={InterfaceUploadIcon}
            isDisabled={isLoading}
            onClick={onClickHandler}
            className={className}
          ></Button>
        </TooltipTrigger>
        <TooltipContent root={null}>{toolTipContent}</TooltipContent>
      </Tooltip>
      <input
        ref={fileInputRef}
        aria-hidden="true"
        type="file"
        name="source_file"
        onChange={onUploadFileHandler}
        accept={supportedFileExtensions.map((ext: string) => `.${ext}`).join(',')}
        disabled={isDisabled ?? false}
        style={{ display: 'none' }}
      />
    </>
  )
}
